import { useSchedulerV3InvalidateQueries } from '../helper-hooks';
import { ProviderMutationContext, useSchedulerV3QueryUpdaters } from '../query-updaters';
import { UpdateProviderIO } from '../types';
import { UseSchedulerV3MutationEndpointArgs, useSchedulerV3Mutation } from './use-scheduler-v3-mutation';

type MutationContext<C = unknown> = {
  // Add internal mutation context here if needed (useful for optimistic updates)
  mutationContext?: ProviderMutationContext;
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `UpdateProvider` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useUpdateProviderMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends UpdateProviderIO['input'] = UpdateProviderIO['input']
>({
  options,
  optimisticUpdate = false,
  ...args
}: UseSchedulerV3MutationEndpointArgs<'UpdateProvider', E, C | undefined, OtherOptions, RequestOverride> = {}) => {
  const {
    invalidateListProviders,
    invalidGetProvider,
    invalidateListCalendarEvents,
    invalidateListCalendarEventsByAttendee,
  } = useSchedulerV3InvalidateQueries();
  const { updateProvider } = useSchedulerV3QueryUpdaters();

  return useSchedulerV3Mutation<'UpdateProvider', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'UpdateProvider',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        // Implement optimistic updates here
        const { _httpOptions, _otherOptions, id, ...providerInfo } = request;

        return {
          mutationContext: optimisticUpdate ? updateProvider(id, providerInfo) : undefined,
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: (response, request, context) => {
        // Implement query updates or invalidation for affected queries here
        const locationId = response.provider.locationId;
        invalidateListProviders(locationId);
        invalidGetProvider(request.id);
        invalidateListCalendarEvents({ locationId, ignoreDate: true });
        invalidateListCalendarEventsByAttendee(locationId);

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        // Revert optimistic update here (if implemented)
        if (context?.mutationContext?.originalData) {
          updateProvider(request.id, context.mutationContext.originalData);
        }

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
